var __jsx = React.createElement;
import React from "react";
import Store from "../../../utils/Store";
import { useStoreState } from "pullstate";
import { useState, useEffect, useRef } from "react";
import AwesomeIcon from "../../AwesomeIcon";
import { faChevronDown, faChevronUp, faLightbulb } from "@fortawesome/pro-regular-svg-icons";
import CustomMarkdown from "../../Chat/CustomMarkdown";
import { useCopilotChat } from "@copilotkit/react-core";

// Simple thinking animation
var ThinkingIcon = function ThinkingIcon(_ref) {
  var _ref$className = _ref.className,
    className = _ref$className === void 0 ? "" : _ref$className;
  return /*#__PURE__*/React.createElement("div", {
    className: "".concat(className)
  }, /*#__PURE__*/React.createElement(AwesomeIcon, {
    name: faLightbulb,
    size: "sm",
    className: "text-teal-500 animate-pulse"
  }));
};
var LiveToolCallsSection = function LiveToolCallsSection(_ref2) {
  var _wsEvents$patientUid;
  var isCurrentMessage = _ref2.isCurrentMessage,
    rawData = _ref2.rawData;
  var _useState = useState(null),
    expandedEvent = _useState[0],
    setExpandedEvent = _useState[1];
  var eventsContainerRef = useRef(null);
  var _useCopilotChat = useCopilotChat(),
    visibleMessages = _useCopilotChat.visibleMessages;

  // Subscribe to store events with real-time updates
  var _useStoreState = useStoreState(Store, function (s) {
      return {
        wsEvents: s.wsEvents,
        patientDetail: s.patientDetail,
        currentPatientsUsingOrchestrator: s.currentPatientsUsingOrchestrator
      };
    }),
    wsEvents = _useStoreState.wsEvents,
    patientDetail = _useStoreState.patientDetail,
    currentPatientsUsingOrchestrator = _useStoreState.currentPatientsUsingOrchestrator;
  var patientUid = patientDetail === null || patientDetail === void 0 ? void 0 : patientDetail.patient_uid;
  var currentPatientUsingOrchestrator = currentPatientsUsingOrchestrator[patientUid];
  var isPatientUsingOrchestrator = (currentPatientUsingOrchestrator === null || currentPatientUsingOrchestrator === void 0 ? void 0 : currentPatientUsingOrchestrator.status) && (currentPatientUsingOrchestrator === null || currentPatientUsingOrchestrator === void 0 ? void 0 : currentPatientUsingOrchestrator.status) !== "completed" && (currentPatientUsingOrchestrator === null || currentPatientUsingOrchestrator === void 0 ? void 0 : currentPatientUsingOrchestrator.status) !== "request_failed" && (currentPatientUsingOrchestrator === null || currentPatientUsingOrchestrator === void 0 ? void 0 : currentPatientUsingOrchestrator.status) !== "timeout" && (currentPatientUsingOrchestrator === null || currentPatientUsingOrchestrator === void 0 ? void 0 : currentPatientUsingOrchestrator.conversation_id) === Store.getRawState().assistantThreadId;
  // Auto-scroll to bottom when new events arrive
  useEffect(function () {
    if (eventsContainerRef.current) {
      eventsContainerRef.current.scrollTop = eventsContainerRef.current.scrollHeight;
    }
  }, [(_wsEvents$patientUid = wsEvents[patientUid]) === null || _wsEvents$patientUid === void 0 ? void 0 : _wsEvents$patientUid.length]);
  if (!isPatientUsingOrchestrator) {
    return null;
  }
  var lastUserMessage = visibleMessages.findLast(function (message) {
    return message.role === "user";
  });
  var isLastUserMessage = (lastUserMessage === null || lastUserMessage === void 0 ? void 0 : lastUserMessage.id) === (rawData === null || rawData === void 0 ? void 0 : rawData.id);
  if (!isLastUserMessage) {
    return null;
  }

  // Show connection status when orchestrator is enabled but no events yet
  if (isPatientUsingOrchestrator && wsEvents[patientUid] && wsEvents[patientUid].length === 0) {
    return /*#__PURE__*/React.createElement("div", {
      className: "mt-3 ml-4"
    }, /*#__PURE__*/React.createElement("div", {
      className: "flex items-center space-x-3"
    }, /*#__PURE__*/React.createElement(ThinkingIcon, null), /*#__PURE__*/React.createElement("span", {
      className: "text-md italic text-gray-600 dark:text-gray-400"
    }, "Thinking...")));
  }

  // Show events list if we have them
  if (wsEvents[patientUid] && wsEvents[patientUid].length > 0) {
    return /*#__PURE__*/React.createElement("div", {
      className: "mt-3 pl-3"
    }, /*#__PURE__*/React.createElement("div", {
      className: "text-sm text-gray-500 dark:text-gray-400 mb-2"
    }, "AI Events (", wsEvents[patientUid].length, ")"), /*#__PURE__*/React.createElement("div", {
      ref: eventsContainerRef,
      className: "max-h-24 overflow-y-auto space-y-1 pr-2"
    }, wsEvents[patientUid].map(function (eventStr, index) {
      var _eventData, _eventData2, _eventData3;
      var eventIndex = index + 1;
      var isExpanded = expandedEvent === eventIndex;
      var eventData;
      var eventLabel = "event";
      try {
        eventData = typeof eventStr === "string" ? JSON.parse(eventStr) : eventStr;
        // Try to get response.role or fallback to "event"
        if (eventData.response && eventData.response.role) {
          eventLabel = eventData.response.role === "tool" ? "agent" : eventData.response.role;
        }
      } catch (error) {
        eventData = {
          raw: eventStr
        };
      }
      return /*#__PURE__*/React.createElement("div", {
        key: "event-".concat(eventIndex, "-").concat(Date.now())
      }, /*#__PURE__*/React.createElement("button", {
        onClick: function onClick() {
          return setExpandedEvent(isExpanded ? null : eventIndex);
        },
        className: "flex items-center space-x-2 text-left w-full hover:bg-gray-50 dark:hover:bg-gray-800 p-1 transition-colors pl-3"
      }, /*#__PURE__*/React.createElement("div", {
        className: "w-1.5 h-1.5 bg-gray-400 dark:bg-gray-500 rounded-full"
      }), /*#__PURE__*/React.createElement("span", {
        className: "text-sm text-gray-600 dark:text-gray-400 font-mono italic"
      }, eventLabel), /*#__PURE__*/React.createElement("span", {
        className: "text-xs text-gray-500 dark:text-gray-500"
      }, "#", eventIndex), ((_eventData = eventData) === null || _eventData === void 0 || (_eventData = _eventData.response) === null || _eventData === void 0 ? void 0 : _eventData.content) && /*#__PURE__*/React.createElement(AwesomeIcon, {
        name: isExpanded ? faChevronUp : faChevronDown,
        size: "xs",
        className: "text-gray-400 dark:text-gray-500"
      })), isExpanded && ((_eventData2 = eventData) === null || _eventData2 === void 0 || (_eventData2 = _eventData2.response) === null || _eventData2 === void 0 ? void 0 : _eventData2.content) && /*#__PURE__*/React.createElement("div", {
        className: "mt-1 ml-4 text-xs font-mono text-gray-700 dark:text-gray-300 whitespace-pre-wrap max-h-32 overflow-y-auto"
      }, JSON.stringify((_eventData3 = eventData) === null || _eventData3 === void 0 || (_eventData3 = _eventData3.response) === null || _eventData3 === void 0 ? void 0 : _eventData3.content, null, 2)));
    })), /*#__PURE__*/React.createElement("div", {
      className: "mt-2 flex items-center space-x-3"
    }, /*#__PURE__*/React.createElement(ThinkingIcon, null), /*#__PURE__*/React.createElement("span", {
      className: "text-sm text-gray-500 dark:text-gray-400 italic"
    }, "Thinking...")));
  }

  // No events yet
  return null;
};
var CustomUserMessage = function CustomUserMessage(props) {
  var _useStoreState2 = useStoreState(Store, function (s) {
      return {
        lastSavedUserMessageId: s.lastSavedUserMessageId
      };
    }),
    lastSavedUserMessageId = _useStoreState2.lastSavedUserMessageId;
  var message = props.message,
    rawData = props.rawData;
  var isCurrentMessage = (rawData === null || rawData === void 0 ? void 0 : rawData.id) === lastSavedUserMessageId;
  return /*#__PURE__*/React.createElement("div", {
    className: "flex flex-col gap-y-2 pr-6 w-full"
  }, /*#__PURE__*/React.createElement("div", {
    className: "flex items-start mb-4 justify-end  pt-4 w-full text-base"
  }, /*#__PURE__*/React.createElement("div", {
    className: "flex flex-col items-end w-full"
  }, /*#__PURE__*/React.createElement("div", {
    className: "flex items-center gap-x-2 rounded-t-lg bg-gray-600 px-4 py-2 text-sm font-semibold text-white dark:bg-gray-700"
  }, "You"), /*#__PURE__*/React.createElement("div", {
    className: "rounded-b-md rounded-tl-md bg-gray-200 px-5 py-2 text-gray-700 dark:bg-white dark:text-gray-800 max-w-full"
  }, /*#__PURE__*/React.createElement(CustomMarkdown, {
    markdown: message !== null && message !== void 0 ? message : ""
  })))), /*#__PURE__*/React.createElement(LiveToolCallsSection, {
    isCurrentMessage: isCurrentMessage,
    rawData: rawData
  }));
};
export default CustomUserMessage;